import { useState, useEffect } from "react";
import { X, Server, Trash2, Plus, Rows3, RadioTower, Package, FileDown, Network, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const KIND_META = {
  olt: { color: "bg-blue-700 border-blue-500", icon: RadioTower },
  patchpanel: { color: "bg-emerald-700 border-emerald-500", icon: Rows3 },
  switch: { color: "bg-amber-700 border-amber-500", icon: Network },
  other: { color: "bg-slate-600 border-slate-400", icon: Package },
};

const TO_TYPE_COLOR = { olt: "#3B82F6", switch: "#F59E0B", patchpanel: "#10B981" };

export default function CabinetView({ cabinet, role, onClose, onRefresh }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [data, setData] = useState(null);
  const [olts, setOlts] = useState([]);
  const [panels, setPanels] = useState([]);
  const [switches, setSwitches] = useState([]);
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ kind: "patchpanel", ref_id: "", name: "", u_start: "", u_size: 1 });

  const reload = () => {
    api.get(`/odf/${cabinet.id}/rack`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/odf/${cabinet.id}/internal-links`).then((r) => setLinks(r.data.links || [])).catch(() => {});
  };
  useEffect(() => {
    reload();
    api.get("/olt").then((r) => setOlts(r.data)).catch(() => {});
    api.get("/patchpanels").then((r) => setPanels(r.data)).catch(() => {});
    api.get("/switches").then((r) => setSwitches(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinet.id]);

  const assign = async () => {
    if (!form.u_start) { toast.error(t("Podaj pozycję U")); return; }
    if ((form.kind === "olt" || form.kind === "patchpanel") && !form.ref_id) { toast.error(t("Wybierz urządzenie")); return; }
    if (form.kind === "other" && !form.name) { toast.error(t("Podaj nazwę")); return; }
    try {
      await api.post(`/odf/${cabinet.id}/rack-assign`, {
        u_start: Number(form.u_start), u_size: Number(form.u_size) || 1,
        kind: form.kind, ref_id: form.ref_id || null, name: form.name || "",
      });
      toast.success(t("Zamontowano w szafie"));
      setForm({ kind: "patchpanel", ref_id: "", name: "", u_start: "", u_size: 1 });
      reload(); onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const remove = async (id) => {
    try { await api.post(`/odf/${cabinet.id}/rack-remove`, { item_id: id }); toast.success(t("Zdemontowano")); reload(); onRefresh && onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Błąd połączenia")); }
  };
  const downloadPassport = async () => {
    try {
      const res = await api.get(`/odf/${cabinet.id}/passport`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `paszport_szafy_${cabinet.number}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("Nie udało się wygenerować PDF")); }
  };

  const height = data?.height_u || 42;
  const items = data?.items || [];
  const topOf = {};    // U -> item (top row of item)
  const covered = {};  // U -> true if occupied (non-top)
  items.forEach((it) => {
    const start = Number(it.u_start), size = Number(it.u_size) || 1;
    const top = start + size - 1;
    for (let u = start; u <= top; u++) { if (u === top) topOf[u] = it; else covered[u] = true; }
    if (size === 1) topOf[top] = it;
  });
  const rows = [];
  for (let u = height; u >= 1; u--) rows.push(u);
  const ROW_H = 26;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="cabinet-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Server size={18} className="text-green-400" /> {t("Szafa")} {cabinet.number} <span className="text-slate-500 text-sm">· {height}U</span></h3>
          <div className="flex items-center gap-2">
            <button onClick={downloadPassport} data-testid="cabinet-passport" className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded px-2.5 py-1 text-xs"><FileDown size={14} /> {t("Paszport PDF")}</button>
            <button onClick={onClose} data-testid="cabinet-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>
        </div>
        {!data ? <div className="p-6 text-slate-500 text-sm">{t("Ładowanie…")}</div> : (
          <div className="overflow-y-auto p-5 grid md:grid-cols-2 gap-6">
            {/* Rack visual */}
            <div>
              <div className="text-[11px] uppercase text-slate-500 mb-2">{t("Widok rack 19\"")}</div>
              <div className="rounded-lg border-2 border-slate-600 bg-slate-950 p-2" data-testid="cabinet-rack">
                {rows.map((u) => {
                  const it = topOf[u];
                  if (covered[u]) return null;
                  if (it) {
                    const meta = KIND_META[it.kind] || KIND_META.other;
                    const Icon = meta.icon;
                    return (
                      <div key={u} className={`relative flex items-center gap-2 rounded border text-white text-xs px-2 my-0.5 ${meta.color}`}
                        style={{ height: (Number(it.u_size) || 1) * ROW_H }} data-testid={`rack-item-${it.id}`}>
                        <span className="font-mono text-[10px] text-white/70 w-8">U{it.u_start}{(Number(it.u_size) || 1) > 1 ? `–${it.u_start + it.u_size - 1}` : ""}</span>
                        <Icon size={14} className="shrink-0" />
                        <span className="truncate flex-1 font-medium">{it.label || it.name}</span>
                        {canEdit && <button onClick={() => remove(it.id)} data-testid={`rack-remove-${it.id}`} className="text-white/70 hover:text-white shrink-0"><Trash2 size={13} /></button>}
                      </div>
                    );
                  }
                  return (
                    <div key={u} className="flex items-center gap-2 rounded border border-dashed border-slate-800 text-slate-600 text-[10px] px-2 my-0.5" style={{ height: ROW_H }}>
                      <span className="font-mono w-8">U{u}</span><span className="opacity-50">{t("wolne")}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Assign form */}
            {canEdit && (
              <div>
                <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1"><Plus size={12} /> {t("Zamontuj urządzenie / przełącznicę")}</div>
                <div className="space-y-2" data-testid="cabinet-assign">
                  <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, ref_id: "", name: "" }))} data-testid="cabinet-kind" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                    <option value="patchpanel">{t("Przełącznica / ODF")}</option>
                    <option value="olt">OLT</option>
                    <option value="switch">{t("Switch")}</option>
                    <option value="other">{t("Inne urządzenie")}</option>
                  </select>
                  {form.kind === "olt" ? (
                    <select value={form.ref_id} onChange={(e) => setForm((f) => ({ ...f, ref_id: e.target.value }))} data-testid="cabinet-ref" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                      <option value="">{t("— OLT —")}</option>
                      {olts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  ) : form.kind === "switch" ? (
                    <select value={form.ref_id} onChange={(e) => setForm((f) => ({ ...f, ref_id: e.target.value }))} data-testid="cabinet-ref" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                      <option value="">{t("— Switch —")}</option>
                      {switches.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  ) : form.kind === "patchpanel" ? (
                    <select value={form.ref_id} onChange={(e) => setForm((f) => ({ ...f, ref_id: e.target.value }))} data-testid="cabinet-ref" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                      <option value="">{t("— przełącznica —")}</option>
                      {panels.map((p) => <option key={p.id} value={p.id}>ODF {p.number} ({p.size_u || 1}U)</option>)}
                    </select>
                  ) : (
                    <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("Nazwa urządzenia")} data-testid="cabinet-name" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min="1" max={height} value={form.u_start} onChange={(e) => setForm((f) => ({ ...f, u_start: e.target.value }))} placeholder={t("Pozycja U (od dołu)")} data-testid="cabinet-ustart" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                    <input type="number" min="1" value={form.u_size} onChange={(e) => setForm((f) => ({ ...f, u_size: e.target.value }))} placeholder={t("Rozmiar [U]")} data-testid="cabinet-usize" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                  </div>
                  <button onClick={assign} data-testid="cabinet-assign-btn" className="w-full bg-green-600 hover:bg-green-500 text-white rounded px-3 py-1.5 text-sm flex items-center justify-center gap-1.5"><Plus size={14} /> {t("Zamontuj")}</button>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">{t("Pozycja U liczona od dołu szafy. Element o rozmiarze >1U zajmie kolejne jednostki w górę.")}</p>
              </div>
            )}
          </div>
        )}
        {data && (
          <div className="px-5 pb-5" data-testid="cabinet-internal-links">
            <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1"><Network size={12} /> {t("Połączenia wewnątrz szafy (patch cordy)")}</div>
            {links.length === 0 ? (
              <div className="text-xs text-slate-500 rounded border border-dashed border-slate-700 px-3 py-2">{t("Brak połączeń między urządzeniami w tej szafie")}</div>
            ) : (
              <div className="space-y-1.5">
                {links.map((lk, i) => (
                  <div key={i} data-testid={`internal-link-${i}`} className="flex items-center gap-2 text-xs bg-slate-900/60 border border-slate-700 rounded px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5 text-emerald-300 font-medium">
                      {lk.fiber_color && <span className="w-2.5 h-2.5 rounded-full inline-block border border-slate-600" style={{ background: lk.fiber_color }} />}
                      {lk.from_label}<span className="text-slate-500 font-mono">:{lk.from_port}</span>
                    </span>
                    <ArrowRight size={14} className="text-pink-400 shrink-0" />
                    <span className="inline-flex items-center gap-1.5 font-medium" style={{ color: TO_TYPE_COLOR[lk.to_type] || "#94A3B8" }}>
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: TO_TYPE_COLOR[lk.to_type] || "#94A3B8" }} />
                      {lk.to_label}{lk.to_port != null && <span className="text-slate-500 font-mono">:{lk.to_port}</span>}
                    </span>
                    <span className="ml-auto text-[10px] uppercase text-slate-500">{lk.to_type === "olt" ? "OLT" : lk.to_type === "switch" ? "Switch" : "ODF"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
